<template>
    <FormSection
        title="Referral Information"
        type="his-section"
        :full-height="false"
        :application-id="Number(application)"
    >
        <template #content>
            <section id="Referral_Source" class="row p-4 pb-0" slot="content">
                <div class="col-12">
                    <b class="me-2">Referral Information</b>
                    <p>
                        Use the filters below to narrow down physicians by city and/or state. To search for a physician,
                        enter the physician's name or NPI in the respective search field.
                    </p>
                </div>
                <section class="form-group col-12 d-flex flex-column">
                    <b class="me-2">Filters</b>
                    <section class="col-12 flex-row py-2 px-3 row g-0 mb-2">
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-source"> City </label>
                            <TypeaheadInput
                                :getData="getReferringPhysicianListItem"
                                :value="referringPhysicianFilters.city"
                                v-on:valueChanged="referringPhysicianFiltersCity"
                                :results-limit="1"
                                :init-value="referringPhysicianFilters.city"
                                itemName="city"
                                placeholder="Select City"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-source"> State </label>
                            <TypeaheadInput
                                :getData="getReferringPhysicianListItem"
                                :value="referringPhysicianFilters.state"
                                v-on:valueChanged="referringPhysicianFiltersState"
                                :init-value="referringPhysicianFilters.state"
                                :results-limit="2"
                                itemName="state"
                                placeholder="Select State"
                            />
                        </div>
                    </section>
                </section>
                <section class="form-group col-12 d-flex flex-column">
                    <b class="me-2">Search</b>
                    <section class="col-12 flex-row px-3 row g-0 mb-2 align-items-center">
                        <div
                            class="form-group card p-2 d-flex col-12 col-lg-6 pe-lg-3 flex-row justify-content-between"
                        >
                            <div class="form-group col-lg-6 ps-2">
                                <label for="referral-source">
                                    Referring Physician
                                    <span class="text-danger">*</span>
                                    <a
                                        tabindex="0"
                                        href="javascript:void(0);"
                                        role="button"
                                        v-custom-popover="{ content: sanitizeMarkup(refPhysicianTooltip) }"
                                    >
                                        <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                    </a>
                                </label>
                                <div>
                                    <TypeaheadInput
                                        :getData="getReferringPhysicianList"
                                        :value="referringPhysicianName"
                                        v-on:valueChanged="referringPhysicianNameChange"
                                        placeholder="Search By Name"
                                        v-on:blurredInput="v$.referral.ReferralInformation.ReferrerPhysician.$touch"
                                        :isError="
                                            v$.referral.ReferralInformation.ReferrerPhysician.$error ||
                                            errors.ReferralInformation.includes('ReferrerPhysician')
                                        "
                                    />
                                </div>
                            </div>
                            <div class="form-check d-flex align-items-center pb-1">
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    :id="`signing-plan-of-care-${refName}`"
                                    v-model="referral.ReferralInformation.IsSigningPlanOfCare"
                                    @change="signingPOCChanged()"
                                />
                                <label class="form-check-label flex-grow-0" :for="`signing-plan-of-care-${refName}`">
                                    <span class="me-1">Certifying Physician</span>
                                    <a
                                        tabindex="0"
                                        href="javascript:void(0);"
                                        role="button"
                                        v-custom-popover="{ content: sanitizeMarkup(signingPOCTooltip) }"
                                    >
                                        <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                    </a>
                                </label>
                            </div>
                        </div>
                        <div class="form-group col-12 col-lg-1 text-center">
                            <strong>OR</strong>
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-source"> NPI </label>
                            <TypeaheadInput
                                :getData="getReferringPhysicianListItem"
                                :value="referringPhysicianFilters.npi"
                                v-on:valueChanged="referringPhysicianFiltersNpiChange"
                                :results-limit="3"
                                itemName="npi"
                                :key-updated="npiChanged('referring')"
                                placeholder="Search By NPI"
                            />
                        </div>
                    </section>
                </section>
                <section class="form-group col-12 d-flex flex-column">
                    <div
                        :class="` ${
                            v$.referral.ReferralInformation.DateOfFaceToFaceVisit.$error ||
                            errors.ReferralInformation.includes('DateOfFaceToFaceVisit')
                                ? 'border border-warning rounded p-2'
                                : ''
                        }`"
                    >
                        <b class="me-2">Face-to-Face Evaluation Information</b>
                        <span class="text-danger">*</span>
                        <a
                            tabindex="0"
                            href="javascript:void(0);"
                            role="button"
                            v-custom-popover="{ content: sanitizeMarkup(faceToFaceTooltip) }"
                        >
                            <i class="fas fa-info-circle text-primary font-size-xs"></i>
                        </a>

                        <div class="form-group p-2 col-12 col-lg-6 pe-lg-3 flex-row">
                            <div class="col-12 col-lg-10 d-flex g-0">
                                <input
                                    type="checkbox"
                                    class="form-check custom-radio pb-1"
                                    v-model="referral.ReferralInformation.FaceToFaceInformationNA"
                                    name="face-to-face-evaluation"
                                    au-target-id="791"
                                    id="evaluationna"
                                />
                                <label class="px-2" for="evaluationna">NA </label>
                            </div>
                            <div class="col-12 col-lg-10 d-flex g-0">
                                <input
                                    type="checkbox"
                                    class="form-check custom-radio pb-1"
                                    v-model="faceToFaceEvaluation"
                                    name="face-to-face-evaluation"
                                    au-target-id="791"
                                    id="evaluationdate"
                                />
                                <label class="px-2" for="evaluationdate"> Date of Face-to-Face Visit </label>
                            </div>
                            <div class="col-12 col-lg-10 d-flex g-0">
                                <div v-if="faceToFaceEvaluation" class="form-group offset-lg-1">
                                    <DatePicker :getDate="onDateChange">
                                        <div class="input-group date datepickerInput">
                                            <input
                                                type="text"
                                                name="DateOfFaceToFaceVisit"
                                                class="form-control"
                                                placeholder="MM/DD/YYYY"
                                                :value="
                                                    formatDateInput(referral.ReferralInformation.DateOfFaceToFaceVisit)
                                                "
                                                @change="handleDateChange"
                                                autocomplete="off"
                                            />
                                            <div class="input-group-addon input-group-append">
                                                <div class="input-group-text">
                                                    <i class="fa fa-calendar"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </DatePicker>
                                </div>
                            </div>
                            <div class="col-12 col-lg-10 d-flex g-0">
                                <input
                                    type="checkbox"
                                    class="form-check custom-radio pb-1"
                                    v-model="referral.ReferralInformation.FaceToFaceToBeCompletedWithin30Days"
                                    name="face-to-face-evaluation"
                                    au-target-id="791"
                                    id="evaluationwithindays"
                                />
                                <label class="px-2" for="evaluationwithindays"
                                    >Face-to-Face to be completed within 30 days
                                </label>
                            </div>
                        </div>
                    </div>
                </section>
                <section class="form-group col-12 d-flex row">
                    <template v-if="showCertifyPhysicianFilters">
                        <div class="col-12">
                            <b class="me-2">Certifying Physician Information</b>
                            <p>
                                Use the filters below to narrow down physicians by city and/or state. To search for a
                                physician, enter the physician's name or NPI in the respective search field.
                            </p>
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <div class="form-check custom-checkbox pb-1">
                                <button
                                    class="card px-2 py-2 cursor-pointer"
                                    :id="`certifying-physician-${refName}`"
                                    @click="showCertifyPhysicianFilters = !showCertifyPhysicianFilters"
                                >
                                    <span class="me-1 text-secondary">Hide Search Parameters</span>
                                </button>
                            </div>
                        </div>
                        <section class="col-12 d-flex flex-column">
                            <b class="me-2">Filters</b>
                            <section class="col-12 flex-row py-2 px-3 row g-0 mb-2">
                                <div class="form-group col-12 col-lg-4 pe-lg-3">
                                    <label for="referral-source"> City </label>
                                    <TypeaheadInput
                                        :getData="getCertifyingPhysicianListItem"
                                        :value="certifyingPhysicianFilters.city"
                                        v-on:valueChanged="certifyingPhysicianFiltersCityChange"
                                        :init-value="certifyingPhysicianFilters.city"
                                        :results-limit="1"
                                        itemName="city"
                                        placeholder="Select City"
                                    />
                                </div>
                                <div class="form-group col-12 col-lg-4 pe-lg-3">
                                    <label for="referral-source"> State </label>
                                    <TypeaheadInput
                                        :getData="getCertifyingPhysicianListItem"
                                        :value="certifyingPhysicianFilters.state"
                                        v-on:valueChanged="certifyingPhysicianFiltersStateChange"
                                        :init-value="certifyingPhysicianFilters.state"
                                        :results-limit="2"
                                        itemName="state"
                                        placeholder="Select State"
                                    />
                                </div>
                            </section>
                        </section>
                    </template>
                    <section class="form-group col-12 d-flex flex-column">
                        <b class="me-2" v-if="showCertifyPhysicianFilters">Search</b>
                        <section
                            class="flex-row row g-0 mb-2 align-items-center"
                            :class="showCertifyPhysicianFilters && 'col-12'"
                        >
                            <div
                                class="form-group card p-2 d-flex col-12 pe-lg-3 col-lg-8 flex-row justify-content-between"
                            >
                                <div class="form-group col-lg-8 ps-2">
                                    <label for="referral-source">
                                        Certifying Physician
                                        <span class="text-danger">*</span>
                                        <a
                                            tabindex="0"
                                            href="javascript:void(0);"
                                            role="button"
                                            v-custom-popover="{ content: sanitizeMarkup(certifyingPhysicianTooltip) }"
                                        >
                                            <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                        </a>
                                    </label>
                                    <div>
                                        <TypeaheadInput
                                            :getData="getCertifyingPhysicianList"
                                            :value="referral.ReferralInformation.CertifyingPhysicianName"
                                            v-on:valueChanged="certifyingPhysicianNameChange"
                                            placeholder="Search By Name"
                                            v-on:blurredInput="
                                                v$.referral.ReferralInformation.CertifyingPhysician.$touch
                                            "
                                            :isError="
                                                v$.referral.ReferralInformation.CertifyingPhysician.$error ||
                                                errors.ReferralInformation.includes('CertifyingPhysician')
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="form-check d-flex align-items-center custom-checkbox pb-1">
                                    <input
                                        type="checkbox"
                                        class="form-check-input"
                                        :id="`referral-attending-physician-${refName}`"
                                        v-model="referral.ReferralInformation.IsPrimaryCarePhysician"
                                    />
                                    <label class="form-check-label" :for="`referral-attending-physician-${refName}`">
                                        <span class="me-1"> Attending Physician </span>
                                        <a
                                            tabindex="0"
                                            href="javascript:void(0);"
                                            role="button"
                                            v-custom-popover="{ content: sanitizeMarkup(attendingPhysicianTooltip) }"
                                        >
                                            <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                        </a>
                                    </label>
                                </div>
                            </div>
                            <template v-if="showCertifyPhysicianFilters">
                                <div class="form-group col-12 col-lg-1 text-center">
                                    <strong>OR</strong>
                                </div>
                                <div class="form-group col-12 col-lg-3 pe-lg-3">
                                    <label for="referral-source"> NPI </label>
                                    <TypeaheadInput
                                        :getData="getCertifyingPhysicianListItem"
                                        :value="certifyingPhysicianFilters.npi"
                                        v-on:valueChanged="certifyingPhysicianFiltersNpiChange"
                                        itemName="npi"
                                        placeholder="Search By NPI"
                                        :results-limit="3"
                                        :key-updated="npiChanged('certifying')"
                                    />
                                </div>
                            </template>
                            <div class="form-group col-12 col-lg-4 pe-lg-3" v-if="!showCertifyPhysicianFilters">
                                <div class="form-check custom-checkbox pb-1">
                                    <button
                                        class="card px-2 py-2 cursor-pointer"
                                        :id="`certifying-physician-${refName}`"
                                        @click="showCertifyPhysicianFilters = !showCertifyPhysicianFilters"
                                    >
                                        <span class="me-1 text-secondary">View More Search Parameters </span>
                                    </button>
                                </div>
                            </div>
                        </section>
                    </section>
                    <section class="col-12 row px-2 g-0">
                        <div class="form-group col-12 col-lg-12 pe-lg-2">
                            <label for="admission-source-name">
                                Admission Source <span class="text-danger">*</span>
                            </label>
                            <select
                                v-model="referral.ReferralInformation.AdmissionSource"
                                :class="`form-select ${
                                    v$.referral.ReferralInformation.AdmissionSource.$error ||
                                    errors.ReferralInformation.includes('AdmissionSource')
                                        ? 'border border-warning rounded p-2'
                                        : ''
                                }`"
                                @blur="v$.referral.ReferralInformation.AdmissionSource.$touch"
                                @change="(e:any)=>handleAllInputChange('AdmissionSource',e.target.value)"
                            >
                                <option value="0">--Select Option--</option>
                                <option
                                    v-for="(admSource, index) of referralOptions?.admissionSource"
                                    :key="index"
                                    :value="admSource.value"
                                >
                                    {{ admSource.text }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-2">
                            <label for="referral-source"> Name of Referral Source</label>
                            <input
                                class="form-control"
                                maxlength="50"
                                type="text"
                                placeholder="Enter Name of Referral Source"
                                v-model="referral.ReferralInformation.ReferralSourceName"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-2">
                            <label for="referral-date">
                                Referral Date
                                <span class="text-danger">*</span>
                            </label>
                            <DatePicker :getDate="onDateChange">
                                <div class="input-group date datepickerInput">
                                    <input
                                        type="text"
                                        name="referral-date-edit"
                                        :class="`form-control ${
                                            v$.referral.ReferralInformation.ReferralDate.$error ||
                                            errors.ReferralInformation.includes('ReferralDate')
                                                ? 'border border-warning'
                                                : ''
                                        }`"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(referral.ReferralInformation.ReferralDate)"
                                        @blur="v$.referral.ReferralInformation.ReferralDate.$touch"
                                        @change="handleDateChange"
                                        autocomplete="off"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                            </DatePicker>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-2">
                            <label for="community-liaison"> Community Liaison </label>
                            <select class="form-select" v-model="referral.ReferralInformation.CommunityLiaison">
                                <option :value="'00000000-0000-0000-0000-000000000000'">--Select Option--</option>
                                <option
                                    v-for="(cmtLiaison, index) of referralOptions.communityLiaison"
                                    :key="index"
                                    :value="cmtLiaison.value"
                                >
                                    {{ cmtLiaison.text }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-2">
                            <label for="internal-referral-source-name"> Internal Referral Source </label>
                            <select class="form-select" v-model="referral.ReferralInformation.InternalReferral">
                                <option :value="'00000000-0000-0000-0000-000000000000'">--Select Option--</option>
                                <option
                                    v-for="(intrlRef, index) of referralOptions.internalReferral"
                                    :key="index"
                                    :value="intrlRef.value"
                                >
                                    {{ intrlRef.text }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-2">
                            <label for="referral-source">
                                <span>Facility Referral Source</span>
                            </label>

                            <select class="form-select" v-model="referral.ReferralInformation.FacilitySource">
                                <option :value="'00000000-0000-0000-0000-000000000000'">--Select Option--</option>
                                <option
                                    v-for="(rflOption, index) of referralOptions.facilitySource"
                                    :key="index"
                                    :value="rflOption.value"
                                >
                                    {{ rflOption.text }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-2">
                            <label for="type-of-inpatient-code"
                                >Type of Inpatient Admission/Visit Code
                                <span class="text-danger">*</span>
                                <a
                                    tabindex="0"
                                    href="javascript:void(0);"
                                    role="button"
                                    v-custom-popover="{ content: sanitizeMarkup(inpatientCodeTooltip) }"
                                >
                                    <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                </a>
                            </label>
                            <select
                                :class="`form-control ${
                                    v$.referral.Insurance.AdditionalBillingInfo.AdmissionType.$error ||
                                    errors.AdditionalBillingInfo.includes('AdmissionType')
                                        ? 'border border-warning'
                                        : ''
                                }`"
                                v-model="referral.Insurance.AdditionalBillingInfo.AdmissionType"
                                @blur="v$.referral.Insurance.AdditionalBillingInfo.AdmissionType.$touch"
                                @change="handleBillingInfo('AdmissionType')"
                            >
                                <option :value="null">--Select Option--</option>
                                <option
                                    v-for="(admType, index) of referralOptions.admissionType"
                                    :key="index"
                                    :value="admType.value"
                                >
                                    {{ admType.text }}
                                </option>
                            </select>
                        </div>
                    </section>
                </section>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./ReferralInformation.ts"></script>
